import { useEffect, useState } from 'react';
import { authedFetch, type SessionState } from '../lib/session.js';
import { navigate } from '../lib/hash-route.js';
import { etiquetaResultado } from '../lib/etiquetas-metricas.js';
import { ETIQUETAS_CATEGORIA_LLAMADA, rotuloPeriodo, type CategoriaLlamada } from '../lib/snapshot-types.js';
import { diaHora } from '../lib/fechas.js';

interface CallRow {
  id: string; ghl_call_id: string;
  contact_name: string | null; contact_company: string | null;
  user_email: string | null;
  /** Nombre de quien la hizo (o "Sin asignar"). */
  persona?: string;
  disposition: string | null; outcome: string | null;
  duration_seconds: number | null; score: number | null;
  started_at: string | null; created_at: string;
  summary: string | null;
}

interface Props {
  session: SessionState;
  disposition: string;
  categoria?: CategoriaLlamada;
  /** El filtro que aplico el snapshot (periodo y personas): la lista suma lo mismo que la tarjeta. */
  filtro: URLSearchParams;
}

interface ListaConfirmada { calls: CallRow[]; total: number | null; truncated: boolean }
function listaDesde(raw: unknown, categoria?: CategoriaLlamada): ListaConfirmada | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.calls) || !r.calls.every((c) => c && typeof c === 'object' && typeof c.id === 'string' && c.id.length > 0)) return null;
  const calls = r.calls as CallRow[];
  if (new Set(calls.map((c) => c.id)).size !== calls.length) return null;
  if (r.total === undefined && r.limit === undefined && r.truncated === undefined) return categoria ? null : { calls, total: null, truncated: false };
  if (!Number.isSafeInteger(r.total) || (r.total as number) < calls.length || !Number.isSafeInteger(r.limit) || (r.limit as number) < 1 || calls.length > (r.limit as number)) return null;
  const total = r.total as number;
  if (calls.length !== Math.min(total, r.limit as number)) return null;
  if (typeof r.truncated !== 'boolean' || r.truncated !== (total > calls.length) || (categoria && r.categoria !== categoria)) return null;
  return { calls, total, truncated: r.truncated };
}

export function CallsList({ session, disposition, categoria, filtro }: Props) {
  const clave = filtro.toString();
  const from = filtro.get('desde') ?? filtro.get('from') ?? undefined;
  const to = filtro.get('hasta') ?? filtro.get('to') ?? undefined;
  const identidad = `${session.token}:${disposition}:${categoria ?? ''}:${clave}`;
  const [lectura, setLectura] = useState<{ identidad: string; lista?: ListaConfirmada; error?: string } | null>(null);
  const actual = lectura?.identidad === identidad ? lectura : null;
  const lista = actual?.lista;
  const calls = lista?.calls;
  const err = actual?.error;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const q = new URLSearchParams(clave);
        // "todas": la tarjeta de llamadas del resumen abre todas las del periodo.
        if (categoria) q.set('categoria', categoria);
        else if (disposition !== 'todas') q.set('disposition', disposition);
        q.set('max', '500');
        const url = `/api/dashboard/calls?${q.toString()}`;
        const r = await authedFetch(session, url);
        if (!r.ok) {
          throw new Error(`calls_${r.status}`);
        }
        const j = listaDesde(await r.json(), categoria);
        if (!j) throw new Error('calls_contract_unconfirmed');
        if (!cancelled) setLectura({ identidad, lista: j });
      } catch (e) {
        if (!cancelled) setLectura({ identidad, error: 'Datos de llamadas no confirmados. No se ha podido leer esta lista.' });
      }
    })();
    return () => { cancelled = true; };
  }, [session.token, disposition, categoria, clave]);

  function num(v: unknown): number | null {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  }

  return (
    <div className="overlay" onClick={() => navigate({ kind: 'snapshot' })}>
      <div className="overlay-card" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
          <div>
            <div className="topbar-brand">{categoria ? `Llamadas · ${ETIQUETAS_CATEGORIA_LLAMADA[categoria]}` : disposition === 'todas' ? 'Llamadas' : `Llamadas · ${etiquetaResultado(disposition)}`}</div>
            <h2 style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 600 }}>
              {lista ? lista.total === null ? `${lista.calls.length} llamada${lista.calls.length === 1 ? '' : 's'} mostrada${lista.calls.length === 1 ? '' : 's'}` : `${lista.total} llamada${lista.total === 1 ? '' : 's'}` : err ? 'Total no confirmado' : 'Cargando…'}
            </h2>
            {from && to ? <div className="topbar-meta" style={{ marginTop: 4 }}>{rotuloPeriodo(from, to)}</div> : null}
            {lista?.total === null && <div className="topbar-meta">Total no confirmado</div>}
          </div>
          <button className="tab" onClick={() => navigate({ kind: 'snapshot' })}>✕ Cerrar</button>
        </div>

        {err && <div className="section-empty" style={{ color: 'var(--danger)' }}>{err}</div>}
        {lista?.truncated && <p className="section-empty calls-list-limite">Mostrando {lista.calls.length} de {lista.total} llamadas. La lista ha alcanzado su límite.</p>}
        {!calls && !err && <div className="section-empty">Cargando…</div>}
        {calls && calls.length === 0 && <div className="section-empty">{lista?.total === null ? 'No se han recibido filas. Total no confirmado.' : categoria || disposition !== 'todas' ? 'Sin llamadas con este resultado en el periodo.' : 'Sin llamadas en este periodo.'}</div>}

        {calls && calls.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {calls.map((c) => (
              <li
                key={c.id}
                className="row-item"
                onClick={() => navigate({ kind: 'call', id: c.id })}
              >
                <div style={{ flex: 1 }}>
                  <div className="row-title">
                    {c.contact_name ?? '(sin nombre)'}
                    {c.contact_company ? <span style={{ fontWeight: 400, color: 'var(--fg-subtle)' }}> · {c.contact_company}</span> : null}
                  </div>
                  {c.summary ? <div className="row-sub" style={{ color: 'var(--fg)' }}>{c.summary.slice(0, 220)}</div> : null}
                </div>
                <div className="row-aside">
                  <div>{c.persona ?? c.user_email ?? 'Sin asignar'}</div>
                  <div>{num(c.duration_seconds) ?? 0} s</div>
                  {num(c.score) !== null && num(c.score)! > 0 && <div>puntuación {num(c.score)!.toFixed(0)}</div>}
                  <div>{diaHora(c.started_at ?? c.created_at)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
